/**
 * Thin wrapper over the shared `createClipHistory` store (`@reflecto/client-core`,
 * spec C2), bound to the web's idb-keyval `StorageAdapter` (`webStorage`) and
 * the web byte budget (`CLIP_HISTORY_BUDGET_WEB`, C3).
 *
 * Persisted PC clipboard history (spec C1) — reverses the prior session-only,
 * in-memory-capped-20 design this module used to implement directly. Keeps
 * the pre-existing exported names (`getClips`/`subscribeClips`/`clearClips`)
 * so call sites (`MainPage`, `notifications.ts`, `disconnect.ts`) churn as
 * little as possible; adds `hydrateClips`/`removeClip`.
 *
 * `getClips()` is a SYNC read of the in-memory mirror — it returns `[]` until
 * `hydrateClips()` has resolved at least once. Call it once at boot (`MainPage`'s
 * mount effect).
 *
 * `createClipHistory`'s own `snapshot()` is memoized (returns the same array
 * reference until the mirror actually changes) and `hydrate()`/`remove()`
 * both `notify()` on every real change, including the initial hydrate — so
 * this wrapper is a pure pass-through for add/remove/clear/hydrate/snapshot,
 * with no caching or notify-shim of its own. (An earlier revision duplicated
 * both guarantees at this layer before client-core grew them; that
 * duplication is gone.)
 *
 * Cross-tab reads (host-level fix, not a client-core gap): `webStorage`
 * (`adapters/storage.web.ts`) broadcasts every write — including a DIFFERENT
 * tab's — to every listener registered via `onChanged`, this tab's included.
 * But `clipHistory.hydrate()` is a one-shot load (`if (hydrated) return`) —
 * client-core exposes no "force re-read from storage" op, and this task is
 * host-only (no `packages/*` changes), so there's no way to make the
 * EXISTING instance re-read. The fix below instead swaps in a fresh,
 * freshly-hydrated `createClipHistory` instance whenever `onChanged` reports
 * a `clipHistory` change this module didn't itself just make (the
 * `myWrites` self-write guard below) — mirroring the "second independent
 * instance over the same adapter" pattern `clipsStore.test.ts`'s reload test
 * already uses to prove persistence. Without the self-write guard this would
 * also fire on this tab's OWN writes (`webStorage`'s `publish()` calls the
 * SAME local `fanOut` a cross-tab `BroadcastChannel` message does), swapping
 * in a not-yet-hydrated instance out from under a caller that just awaited
 * `addClip()` and expects `getClips()` to see it immediately.
 *
 * Same-tab write vs. in-flight swap (FIX 2 — see `pendingSwap` below): a
 * cross-tab write's swap runs a `next.hydrate()` storage READ that can take
 * a tick to resolve. If THIS tab's own `addClip`/`removeClip`/`clearClips`
 * dispatched against the old (about-to-be-orphaned) instance while that read
 * is in flight, the swap would reassign `clipHistory = next` using a
 * snapshot that predates the local write — silently dropping it from memory,
 * and then from storage too the next time anything persists through the new
 * instance (`persist()` always writes the WHOLE mirror array, so it has no
 * way to know a byte it never read about is missing). Every write below
 * joins any `pendingSwap` first (the same pattern `hydrateClips()` already
 * used), so a write that arrives mid-swap is deferred until the swap has
 * fully landed and then dispatched against the CURRENT (post-swap)
 * instance — never lost, and no separate "am I stale?" check needed.
 *
 * Self-write detection by reference, not by a duration counter (SHOULD-FIX,
 * round 3): an earlier revision used a `selfWriteDepth` counter incremented
 * for the ENTIRE span of this module's own add()/remove()/clear() call —
 * including client-core's internal `await hydrate()` and `persist()`'s
 * `await storage.set(...)` (itself a `getMany` → `setMany` round-trip,
 * several ticks). Any genuinely different tab's write landing via
 * `onChanged` inside that window was indistinguishable from "some write of
 * mine is in flight" and got discarded outright — no swap, so this tab
 * never learned about it, and its own (by-then-stale) mirror would go on to
 * overwrite storage on its NEXT write, permanently erasing the other tab's
 * entry. A duration counter can only ever answer "is some write of mine
 * outstanding", never "is THIS SPECIFIC event mine" — the two questions
 * diverge exactly when a foreign write overlaps a local one, which is the
 * case that matters. `myWrites` (below) answers the narrower question
 * directly: it holds the exact array reference each in-flight `set()` call
 * is writing, populated by `trackedStorage()`'s wrapper immediately before
 * delegating to the real `webStorage.set()`. `webStorage`'s local `fanOut()`
 * (same-tab echo) delivers that EXACT reference, uncloned, straight from
 * `partial.clipHistory` to `changes.clipHistory.newValue` — so
 * `myWrites.has(newValue)` is `true` for a genuine self-echo and `false`
 * for anything else (a different `createClipHistory` instance's own array,
 * or a real cross-tab `BroadcastChannel` delivery, which arrives
 * structurally cloned and can never be reference-equal to anything this
 * module built) — independent of how long any of our own writes have been
 * running, closing the round-3 gap without widening or narrowing a window.
 *
 * Reference identity alone isn't the whole fix, though: correctly
 * recognizing a foreign write as foreign only means the SWAP starts — it
 * doesn't retroactively move an ALREADY-DISPATCHED local write onto the
 * post-swap instance. If `addClip`'s own `.add()` call was already running
 * against the pre-swap instance when the foreign write landed (this tab had
 * no pending swap to join yet at dispatch time — `withPendingSwap` can't
 * defer a call that already started), that call's `persist()` still
 * finishes writing its (now stale, "theirs"-less) snapshot straight to
 * storage once released, clobbering the swap's result. `dispatch()` (below)
 * closes this: after its op settles, it checks whether `clipHistory` moved
 * on to a different instance WHILE the op was running and, if so, replays
 * the same logical operation against whatever is current now. Replay is
 * always safe (add dedups by id, remove on an absent id is a no-op, clear
 * is idempotent) and always wins the race by construction — it only starts
 * once the original write's own persist() has FULLY settled, so it is
 * always the last physical write, never a coin flip against the swap's own
 * timing. Looped (not a single retry) so a swap landing during the replay
 * itself is reconciled too.
 *
 * SECURITY: entries hold decrypted plaintext (C1). Never log an entry or any
 * part of its `content` — failures here log nothing at all (matches the
 * shared factory's own no-log contract).
 */

import type { StorageAdapter } from "@reflecto/client-core";
import {
  CLIP_HISTORY_BUDGET_WEB,
  type ClipHistoryApi,
  type ClipHistoryEntry,
  createClipHistory,
} from "@reflecto/client-core";
import { webStorage } from "../adapters/storage.web";

export type { ClipHistoryEntry };

// Exact array references THIS module is currently in the middle of writing
// to storage — see the module docblock's "Self-write detection by
// reference" section for the full rationale. Populated by trackedStorage()'s
// wrapped set() below, immediately before delegating to the real
// `webStorage.set()`, and read (only) by the `onChanged` handler.
const myWrites = new Set<ClipHistoryEntry[]>();

/**
 * Wraps `webStorage` so every `set()` call made by a `createClipHistory`
 * instance this module creates (see `newClipHistory()`) registers its exact
 * `clipHistory` array in `myWrites` for the call's duration. Only `set()` is
 * overridden — `get()`/`remove()`/`clear()`/`onChanged()` pass straight
 * through to the real `webStorage`, which is all `createClipHistory` ever
 * calls on the adapter it's given besides `set()`/`get()`.
 */
function trackedStorage(): StorageAdapter {
  return {
    ...webStorage,
    async set(partial) {
      const arr = partial.clipHistory;
      if (arr) myWrites.add(arr);
      try {
        return await webStorage.set(partial);
      } finally {
        if (arr) myWrites.delete(arr);
      }
    },
  };
}

function newClipHistory(): ClipHistoryApi {
  return createClipHistory(trackedStorage(), { budgetBytes: CLIP_HISTORY_BUDGET_WEB });
}

// The active instance. Reassigned wholesale on a detected cross-tab change
// (see the docblock) — every export below reads this closure variable at
// CALL time, so a swap is transparent to already-issued calls (which keep
// running against the instance object they were dispatched on) and visible
// to the next call.
let clipHistory = newClipHistory();

// Host-level listener registry, independent of which `clipHistory` instance
// is currently active, so a `subscribeClips` caller's subscription survives
// a cross-tab swap without needing to resubscribe. Forwarded-to by
// `forwardFrom` below, which is re-pointed at the new instance on every swap.
const listeners = new Set<() => void>();

function notifyListeners(): void {
  for (const listener of listeners) listener();
}

function forwardFrom(instance: ClipHistoryApi): () => void {
  return instance.subscribe(notifyListeners);
}

let unsubscribeForwarding = forwardFrom(clipHistory);

// Set while a cross-tab swap (below) is building + hydrating its replacement
// instance. Joined by `withPendingSwap` — used by every write below plus
// `hydrateClips()` — so nothing dispatches against the about-to-be-orphaned
// outgoing instance (see the module docblock's "same-tab write vs. in-flight
// swap" paragraph for the full rationale).
let pendingSwap: Promise<void> | undefined;

// Monotonic generation counter for the swap below, incremented each time a
// new one starts and captured (as `myGeneration`) BEFORE `next.hydrate()`'s
// await. Two `onChanged` events close enough together start two overlapping
// swaps; without this, whichever `next.hydrate()` happens to RESOLVE last
// wins unconditionally (`clipHistory = next`), even if it started FIRST and
// is by now stale — a real risk in principle even though nothing forces it
// today (IndexedDB serializes overlapping-scope transactions, so a swap's
// own read can't itself observe stale data). The check below makes "newest
// started wins" an invariant this module asserts, not one it merely
// inherits from browser scheduling.
let swapGeneration = 0;

webStorage.onChanged((changes) => {
  if (!("clipHistory" in changes)) return;
  const newValue = changes.clipHistory?.newValue;
  // Our own write's echo — mirror already reflects it. Reference check, not
  // a duration counter (see the module docblock's "Self-write detection by
  // reference" section): `undefined` (e.g. a whole-storage `webStorage.clear()`
  // that bypasses this module's writes entirely) is never a value `myWrites`
  // holds, so it correctly falls through to "treat as external" below.
  if (newValue !== undefined && myWrites.has(newValue)) return;
  // A different tab (or the SW) wrote clipHistory — build and fully hydrate a
  // fresh instance before swapping it in (module docblock has the
  // self-write-guard / pendingSwap rationale; this comment covers only what's
  // specific to the swap's own ordering). Swapping the `clipHistory` binding
  // FIRST, before `next` is hydrated, would leave a window where a
  // concurrent `getClips()` reads a brand-new, empty instance — a spurious
  // blank-list flash right after another tab wrote. Keeping the OLD
  // (merely one write stale) instance active until `next` is ready avoids
  // that. Re-point the forwarding subscription so already-registered
  // `subscribeClips` listeners survive the swap without resubscribing, then
  // `notifyListeners()` explicitly — `next`'s own internal notify (fired
  // inside `hydrate()`) ran before anything was forwarding from it, so
  // nobody would otherwise learn the swap occurred. The whole chain is
  // wrapped in one `.catch` so a throwing subscriber inside
  // `notifyListeners()` can't escape as an unhandled rejection (`hydrate()`
  // itself never rejects today — it swallows read errors — but the guard
  // costs nothing and covers that too).
  const myGeneration = ++swapGeneration;
  const next = newClipHistory();
  const swap = next
    .hydrate()
    .then(() => {
      // A newer swap has since started — let IT win. Applying this stale
      // result would clobber a mirror that has already moved on (see
      // `swapGeneration`'s docblock).
      if (myGeneration !== swapGeneration) return;
      unsubscribeForwarding();
      clipHistory = next;
      unsubscribeForwarding = forwardFrom(clipHistory);
      notifyListeners();
    })
    .catch(() => {
      // Keep the outgoing instance active rather than swapping to one that
      // never finished populating its mirror.
    });
  pendingSwap = swap;
  void swap.then(() => {
    // Only clear if nothing newer has started in the meantime (reference
    // equality against the exact promise this invocation kicked off).
    if (pendingSwap === swap) pendingSwap = undefined;
  });
});

/**
 * Join a pending cross-tab swap (if any) before running `fn`, so `fn` always
 * dispatches against the CURRENT `clipHistory` instance rather than one
 * about to be replaced. Every write below (`addClip`/`removeClip`/
 * `clearClips`) and `hydrateClips()` go through this — see the module
 * docblock's "same-tab write vs. in-flight swap" paragraph for why.
 */
function withPendingSwap<T>(fn: () => Promise<T>): Promise<T> {
  const swap = pendingSwap;
  return swap ? swap.then(fn) : fn();
}

/**
 * One-time load into the sync mirror. Idempotent and safe to call from
 * multiple mount sites (concurrent callers share one underlying load).
 */
export function hydrateClips(): Promise<void> {
  return withPendingSwap(() => clipHistory.hydrate());
}

/**
 * Current most-recent-first snapshot, referentially stable between calls
 * until the next add/remove/clear/hydrate (client-core's own memoization —
 * see the module docblock). Empty until `hydrateClips()` (or another op,
 * which hydrates internally) has resolved at least once.
 */
export function getClips(): readonly ClipHistoryEntry[] {
  return clipHistory.snapshot();
}

/**
 * Run `op` against the currently active `clipHistory` instance, joining any
 * ALREADY-pending cross-tab swap first (`withPendingSwap`) so a call made
 * while a swap is in flight starts fresh against the post-swap instance.
 *
 * That alone doesn't cover a swap that STARTS mid-call: `target` is captured
 * once `withPendingSwap` lets `op` run, and if a genuinely different tab's
 * write lands (and is correctly recognized as foreign via `myWrites`, not
 * mistaken for this call's own echo) while `op(target)` is still awaiting
 * its own `persist()`, `clipHistory` moves on to a new instance that never
 * saw this call's effect. The loop below detects that after `op` settles and
 * REPLAYS the same operation against whatever is current now — see the
 * module docblock's "Reference identity alone isn't the whole fix" section
 * for why this is always safe (add/remove/clear are all idempotent) and
 * always correct (a replay only starts once the original write has fully
 * landed, so it's always the last physical write, never racing the swap for
 * who lands last).
 */
function dispatch(op: (instance: ClipHistoryApi) => Promise<void>): Promise<void> {
  return withPendingSwap(async () => {
    let target = clipHistory;
    await op(target);
    while (clipHistory !== target) {
      target = clipHistory;
      await op(target);
    }
  });
}

/**
 * Record one clip — inbound (`direction: "received"`) or outbound
 * (`direction: "sent"`). Dedups by `id`; callers that want SSE-redelivery
 * safety for a received clip should pass the wire `msgId` as `id`.
 */
export function addClip(entry: ClipHistoryEntry): Promise<void> {
  return dispatch((instance) => instance.add(entry));
}

/** Delete one clip from history. Unknown id is a no-op. */
export function removeClip(id: string): Promise<void> {
  return dispatch((instance) => instance.remove(id));
}

/**
 * Drop every clip. Called on disconnect/pairing-lost (wired into
 * `clearLocalState`) so a re-pair starts with an empty history.
 */
export function clearClips(): Promise<void> {
  return dispatch((instance) => instance.clear());
}

/** `useSyncExternalStore` subscribe — fires on add/remove/clear (this tab or another); returns an unsubscribe fn. */
export function subscribeClips(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
